import { useCallback, useRef, type ReactNode } from 'react';
import type { AdminCropTransform } from '@/lib/adminImageCrop';

type Props = {
  transform: AdminCropTransform;
  onTransformChange: (patch: Partial<AdminCropTransform>) => void;
  disabled?: boolean;
  aspectRatio: string;
  children: ReactNode;
};

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

export function AdminCropGestureViewport({
  transform,
  onTransformChange,
  disabled = false,
  aspectRatio,
  children,
}: Props) {
  const boxRef = useRef<HTMLDivElement>(null);
  const pinchStart = useRef<{ dist: number; zoom: number } | null>(null);
  const panStart = useRef<{ x: number; y: number; panX: number; panY: number } | null>(null);

  const transformRef = useRef(transform);
  transformRef.current = transform;

  const applyZoom = useCallback(
    (zoom: number) => {
      onTransformChange({ zoom: clamp(zoom, 1, 3) });
    },
    [onTransformChange],
  );

  const applyPan = useCallback(
    (panX: number, panY: number) => {
      onTransformChange({
        panX: clamp(panX, -100, 100),
        panY: clamp(panY, -100, 100),
      });
    },
    [onTransformChange],
  );

  const onTouchStart = (e: React.TouchEvent) => {
    if (disabled) return;
    if (e.touches.length >= 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      pinchStart.current = { dist: Math.hypot(dx, dy) || 1, zoom: transformRef.current.zoom };
      panStart.current = null;
    } else if (e.touches.length === 1) {
      panStart.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
        panX: transformRef.current.panX,
        panY: transformRef.current.panY,
      };
      pinchStart.current = null;
    }
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (disabled) return;
    if (e.touches.length >= 2 && pinchStart.current) {
      e.preventDefault();
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.hypot(dx, dy) || 1;
      applyZoom(pinchStart.current.zoom * (dist / pinchStart.current.dist));
    } else if (e.touches.length === 1 && panStart.current) {
      e.preventDefault();
      const rect = boxRef.current?.getBoundingClientRect();
      if (!rect?.width || !rect.height) return;
      const dx = e.touches[0].clientX - panStart.current.x;
      const dy = e.touches[0].clientY - panStart.current.y;
      applyPan(
        panStart.current.panX + (dx / rect.width) * 200,
        panStart.current.panY + (dy / rect.height) * 200,
      );
    }
  };

  const onTouchEnd = () => {
    pinchStart.current = null;
    panStart.current = null;
  };

  const onWheel = (e: React.WheelEvent) => {
    if (disabled) return;
    e.preventDefault();
    applyZoom(transformRef.current.zoom + (e.deltaY > 0 ? -0.08 : 0.08));
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (disabled || e.pointerType === 'touch') return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    panStart.current = {
      x: e.clientX,
      y: e.clientY,
      panX: transformRef.current.panX,
      panY: transformRef.current.panY,
    };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (disabled || !panStart.current || e.pointerType === 'touch') return;
    const rect = boxRef.current?.getBoundingClientRect();
    if (!rect?.width || !rect.height) return;
    const dx = e.clientX - panStart.current.x;
    const dy = e.clientY - panStart.current.y;
    applyPan(
      panStart.current.panX + (dx / rect.width) * 200,
      panStart.current.panY + (dy / rect.height) * 200,
    );
  };

  const onPointerUp = (e: React.PointerEvent) => {
    if (e.pointerType === 'touch') return;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* already released */
    }
    panStart.current = null;
  };

  return (
    <div
      ref={boxRef}
      className="relative w-full overflow-hidden rounded-xl touch-none select-none"
      style={{
        aspectRatio,
        background: 'rgba(255,255,255,0.05)',
        border: '1px solid rgba(255,255,255,0.12)',
        touchAction: 'none',
      }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchEnd}
      onWheel={onWheel}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      {children}
    </div>
  );
}

